import type * as THREE from 'three';
import type { ColliderHandle, RoomCapabilities, RoomEnvironment, RoomModule, RoomModuleDefinition, RoomOptions, RoomView, Underfoot } from '@/game/roomModule';
import { getZone } from '@/render/palette';
import { BAY_Z, BORN_SPAWN, LAMP_AT, bayShadowLine, bornBayFixtures, bornBaySolids, createBornBay } from '@/world/bornBay';
import {
  FARROWING_SPAWN, FARROWING_ZONE_ID, HALL, createFarrowingHall, farrowingAmbience, farrowingChamferCorners, farrowingFixtures, farrowingSolids,
} from '@/world/farrowing';
import { soleSolidWithRole } from '@/world/solidRoles';

/**
 * The farrowing hall, where beats 1 and 2 happen, as a room module. Until
 * session 82 it was the first and longest construction branch in `main.ts`.
 *
 * **The room builds her bay; the beats run it.** Beats 1 and 2 reach the bay
 * and the two colliders a beat removes through `RoomModule.farrowing`. The
 * room also owns the two things only it can answer: which floor she is on
 * (`underfoot`, straw in the pens, concrete on the walkway) and the
 * distant-batten cull (`?battens=N`), which follows the lens through
 * `renderStep`'s view. Both lived in `main.ts` until session 86.
 *
 * `?dress=0` is not honoured, as the legacy branch never honoured it.
 */

function farrowingEnvironment(options: RoomOptions): RoomEnvironment {
  const zone = getZone(FARROWING_ZONE_ID);
  if (zone === undefined) throw new Error(`unknown zone "${FARROWING_ZONE_ID}"`);
  return {
    zone,
    lighting: {
      // The bay's heat lamp joins the hall's battens rather than being a second
      // lighting rig. One rig owns the exposure reference (D-022), and a second
      // place that creates lights is a second place that can disagree with it.
      fixtures: [...farrowingFixtures(), ...bornBayFixtures()],
      // The key comes down the hall from behind the far wall rather than from a
      // window, because there are no windows. It is a lie that buys the one
      // thing a row of ceiling battens cannot: shadows long enough to read the
      // shape of what cast them, which is how the crate rows resolve at all.
      keyDirection: { x: 0.3, y: 0.88, z: -0.36 },
      keyIntensity: 0.85,
      shadowExtent: 11,
      hemiIntensity: 0.34,
      ambientIntensity: 0.12,
      // Well above key + hemi + ambient, because the battens are the room's real
      // light source and deliver more than all three together to the walkway.
      // Anchoring on the defaults clamped every surface to the top band and the
      // hall rendered as one flat tone. See ZoneLightingOptions.exposureReference.
      exposureReference: 2.4,
    },
    background: { kind: 'rig' },
    // She wakes in the straw, not on the walkway. `FARROWING_SPAWN` survives as
    // the hall's own spawn for `?beat=0`, the hall with no beat in it.
    spawn: options.beat === 0 ? FARROWING_SPAWN : BORN_SPAWN,
    ambience: farrowingAmbience(),
  };
}

/**
 * The kerb is at `HALL.walkway + 0.12`: she is in a pen on straw beyond it, on
 * the walkway's concrete inside it. One test for both, see `Underfoot`.
 */
const IN_PEN: Underfoot = Object.freeze({ surface: 'straw', pen: true });
const ON_WALKWAY: Underfoot = Object.freeze({ surface: 'concrete', pen: false });
const KERB_X = HALL.walkway + 0.12;

/**
 * `?battens=N` — the distant-batten cull, and it is an EXPERIMENT (S1).
 *
 * D-027 established that this hardware is bound by lights per fragment, and
 * Three's forward path has no per-object light culling: every visible light is
 * in the uniform array for every material, so the fragment cost is close to
 * linear in visible point lights. The dressed hall runs six of them — five
 * battens plus the heat lamp — and beat 1 happens entirely under one.
 *
 * **Why it keeps a CONSTANT number lit instead of just switching far ones
 * off.** `numPointLights` is a program cache key in Three
 * (`WebGLPrograms.js:473`) and `NUM_POINT_LIGHTS` is substituted into the
 * shader source (`WebGLProgram.js:224`). Changing how many lights are visible
 * therefore recompiles every lit material in the scene. A cull that switched
 * battens off as she walked would hitch far worse than the cost it saves.
 * Keeping exactly N lit and only swapping WHICH ones holds the count fixed, so
 * no recompile ever happens — `object.visible === false` still drops the light
 * before `pushLight` (`WebGLRenderer.js:1833`), which is what makes the saving
 * real. Gated in the browser by `render-budget.spec.ts` (*the batten cull keeps
 * the visible light count constant*) and in the sim lane by
 * `room-module.spec.ts`.
 *
 * Absent (the default) is every batten, so the shipped image is unchanged and
 * beat 1's R-018 staging cannot move underneath this. Sorted nearest-first per
 * frame, so it follows the camera down the hall rather than assuming beat 1's
 * end of the room.
 */
function battenCull(battens: readonly THREE.PointLight[], requested: number | undefined): ((view: RoomView) => void) | null {
  const budget = requested === undefined ? battens.length : Math.max(1, Math.min(battens.length, requested));
  if (budget >= battens.length) return null;
  // Reused so a per-frame sort allocates nothing.
  const byDistance = battens.map((light) => ({ light, d2: 0 }));
  return (view) => {
    for (const entry of byDistance) {
      const dx = entry.light.position.x - view.camera.x;
      const dz = entry.light.position.z - view.camera.z;
      entry.d2 = dx * dx + dz * dz;
    }
    byDistance.sort((a, b) => a.d2 - b.d2);
    for (let i = 0; i < byDistance.length; i++) {
      const entry = byDistance[i];
      if (entry) entry.light.visible = i < budget;
    }
  };
}

function createFarrowingRoom(capabilities: RoomCapabilities, battenBudget: number | undefined): RoomModule {
  const hall = createFarrowingHall();
  capabilities.scene.add(hall);

  // The heat lamp's light, found by where it is rather than by index: it is
  // the only light in the room that is not on the walkway centreline, so its
  // position identifies it unambiguously (see `rigPointLights`).
  let lampLight: THREE.PointLight | null = null;
  const battens: THREE.PointLight[] = [];
  // The batten segments near enough to her bay to light it. These, not the
  // heat lamp, are why the creep never went dark when somebody leaned over it
  // — see `BornBay.nearLights`. Selected by distance in Z, for the reason
  // the lamp is found by position.
  const nearLights: THREE.PointLight[] = [];
  for (const light of capabilities.rigPointLights) {
    if (Math.abs(light.position.x - LAMP_AT.x) < 0.01 && Math.abs(light.position.z - LAMP_AT.z) < 0.01) {
      lampLight = light;
      continue;
    }
    battens.push(light);
    // 1.6 m either side of the bay centre. One bay's worth of ceiling: far
    // enough to include whatever is directly overhead, short enough that the
    // hall beyond her stays lit while her own pen goes dark.
    if (Math.abs(light.position.z - BAY_Z) <= 1.6) nearLights.push(light);
  }

  // The bay is built with or without a beat: it is CONTENT, and the hall
  // without her mother in it is not a room anybody should be looking at. What
  // `?beat=0` turns off is the beat — the camera hold, the boots, the
  // interactables and the sequencer — not the scene.
  const lampFullIntensity = lampLight === null ? 0 : lampLight.intensity;
  const bay = createBornBay(lampLight, nearLights);
  capabilities.scene.add(bay.group);
  const shadowLine = bayShadowLine();
  capabilities.scene.add(shadowLine);

  // Colliders from the SAME list the meshes come from, so what you can see and
  // what she can stand on cannot drift apart. The pen rail is solid to her and
  // invisible to the camera sweep — its authored `camera: 'passes'`, which the
  // host's `colliders.add` honours: a 1.03 m frame down both sides of a 1.4 m
  // walkway sits exactly where the follow lens rides, and without it the arm
  // collapses from 1.370 m to 0.550 whenever she walks near a kerb.
  //
  // Which colliders are kept as handles are the solids' authored `role`
  // fields, not their names (audit M2, `solid-roles.spec.ts`). The role
  // lookups throw here, at construction, if a list stops carrying exactly one.
  const hallSolids = farrowingSolids();
  const shutGateSolid = soleSolidWithRole(hallSolids, 'shut-pen-gate', 'farrowingSolids()');
  const shutDoorSolid = soleSolidWithRole(hallSolids, 'walkway-door', 'farrowingSolids()');
  const baySolids = bornBaySolids();
  const takenSiblingSolid = soleSolidWithRole(baySolids, 'taken-sibling', 'bornBaySolids()');
  const colliders: ColliderHandle[] = [];
  let shutGate: ColliderHandle | null = null;
  let shutDoor: ColliderHandle | null = null;
  let takenSibling: ColliderHandle | null = null;
  for (const solid of hallSolids) {
    const handle = capabilities.colliders.add(solid);
    // Kept so beat 2 can throw it away. The hall's own gate collider is the
    // SHUT one and is axis-aligned, which is what lets it be an ordinary
    // `HallSolid` — but a gate that opens has to rotate. Beat 2 replaces this
    // with one built in leaf-local extents; every other beat gets a shut gate
    // and a closed pen, which is exactly what `pen-containment.spec.ts` asserts.
    if (solid === shutGateSolid) shutGate = handle;
    // The walkway door, the same way: shut for every beat but 2, which swings
    // it and leaves it ajar for her.
    else if (solid === shutDoorSolid) shutDoor = handle;
    else colliders.push(handle);
  }
  // The bay's own solids. Most are invisible: the crate, the slats and the
  // kerb are already drawn by the hall's instanced dressing, and what was
  // missing was only the physics.
  for (const solid of baySolids) {
    const handle = capabilities.colliders.add(solid);
    if (solid === takenSiblingSolid) takenSibling = handle;
    else colliders.push(handle);
  }
  // The sluice's sloped lips, as convex hulls of the same six corners their
  // meshes are built from — so what she can walk up and what you can see are
  // the same solid.
  for (const side of [-1, 1] as const) {
    colliders.push(capabilities.colliders.addHull(new Float32Array(farrowingChamferCorners(side).flat())));
  }

  const removeTakenSiblingCollider = (): void => {
    if (takenSibling === null) return;
    capabilities.colliders.remove(takenSibling);
    takenSibling = null;
  };
  const removeShutGateCollider = (): void => {
    if (shutGate === null) return;
    capabilities.colliders.remove(shutGate);
    shutGate = null;
  };
  const removeWalkwayDoorCollider = (): void => {
    if (shutDoor === null) return;
    capabilities.colliders.remove(shutDoor);
    shutDoor = null;
  };
  const walkwayDoorHinge = hall.getObjectByName('walkway-door')?.getObjectByName('door-hinge');
  if (!walkwayDoorHinge) throw new Error('farrowing room: the hall has no walkway door hinge');
  const cullBattens = battenCull(battens, battenBudget);
  let disposed = false;
  return {
    interactables: [],
    fixedStep(): void { /* static: beats 1 and 2 own everything that moves */ },
    renderStep(_frameDt, view): void { cullBattens?.(view); },
    underfoot: (x) => (Math.abs(x) > KERB_X ? IN_PEN : ON_WALKWAY),
    framing: () => null,
    diagnostics: () => ({ kind: 'none' }),
    farrowing: { bay, lampFullIntensity, removeTakenSiblingCollider, removeShutGateCollider, walkwayDoorHinge, removeWalkwayDoorCollider },
    dispose(): void {
      if (disposed) return;
      disposed = true;
      for (const handle of colliders) capabilities.colliders.remove(handle);
      removeTakenSiblingCollider();
      removeShutGateCollider();
      removeWalkwayDoorCollider();
      capabilities.scene.remove(hall);
      capabilities.scene.remove(bay.group);
      capabilities.scene.remove(shadowLine);
    },
  };
}

export const FARROWING_ROOM_MODULE: RoomModuleDefinition = {
  room: 'farrowing',
  environment: farrowingEnvironment,
  create: (options, capabilities) => createFarrowingRoom(capabilities, options.battens),
};
